import {
  MdOutlineDashboard,
  MdPersonOutline,
  MdOutlineBook,
  MdOutlineAdminPanelSettings,
  MdHistory,
  MdOutlineAccountBox,
  MdOutlineLoyalty,
  MdOutlineWarningAmber,
  MdOutlineHome,
  MdOutlineSettings,
  MdOutlineDiscount,
  MdOutlineAccountBalanceWallet,
} from 'react-icons/md';
import { NavItem } from '@/components/myaccount/account-navigation/AccountNavigation.types';
import { MY_ACCOUNT_ROUTES, PUBLIC_ROUTES } from '@/routes/routes.constants';

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'overview',
    label: 'Overview',
    labelKey: 'overview',
    icon: <MdOutlineDashboard className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.overview,
  },
  {
    id: 'general',
    label: 'General',
    labelKey: 'general',
    icon: <MdPersonOutline className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.profile,
  },
  {
    id: 'reservations',
    label: 'Reservations',
    labelKey: 'reservations',
    icon: <MdOutlineBook className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.reservations,
  },
  {
    id: 'manage-permit',
    label: 'Manage Permit',
    labelKey: 'managePermit',
    icon: <MdOutlineAdminPanelSettings className="tw:text-font" size={24} />,
    // Land on the first tab (Accounts), not Autopay. PB-2739
    href: MY_ACCOUNT_ROUTES.managePermitAccounts,
  },
  {
    id: 'parking-credits',
    label: 'Parking Credits',
    labelKey: 'parkingCredits',
    icon: <MdOutlineAccountBalanceWallet className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.parkingCredits,
  },
  {
    id: 'parking-history',
    label: 'Parking History',
    labelKey: 'parkingHistory',
    icon: <MdHistory className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.parkingHistory,
  },
  {
    id: 'parking-pass',
    label: 'Parking Pass',
    labelKey: 'parkingPass',
    icon: <MdOutlineAccountBox className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.guestPass,
  },
  {
    id: 'loyalty',
    label: 'Loyalty',
    labelKey: 'loyalty',
    icon: <MdOutlineLoyalty className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.loyalty,
  },
  {
    id: 'citations',
    label: 'Citations',
    labelKey: 'citations',
    icon: <MdOutlineWarningAmber className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.citations,
  },
  {
    id: 'parking-validation',
    label: 'Parking Validation',
    labelKey: 'parkingValidation',
    icon: <MdOutlineDiscount className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.parkingValidation,
  },
  {
    id: 'settings',
    label: 'Settings',
    labelKey: 'settings',
    icon: <MdOutlineSettings className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.settings,
  },
];

export type NavItemId = NavItem[][number]['id'];

/** Nav id whose label follows the tenant's configured Parking Pass title. */
export const PARKING_PASS_NAV_ID = 'parking-pass';

/**
 * Overrides the Parking Pass nav item's label with the tenant's resolved pass
 * title (the same value the Guest Pass page tabs/heading use), leaving every
 * other item untouched. Returns a new array; inputs are not mutated.
 */
export function withParkingPassNavLabel(items: NavItem[], passTitle: string): NavItem[] {
  return items.map((item) =>
    item.id === PARKING_PASS_NAV_ID ? { ...item, displayLabel: passTitle } : item
  );
}

export const MOBILE_NAV_ITEMS = [
  {
    id: 'parking-history',
    label: 'Parking History',
    labelKey: 'parkingHistory',
    icon: <MdHistory className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.parkingHistory,
  },
  {
    id: 'parking-pass',
    label: 'Parking Pass',
    labelKey: 'parkingPass',
    icon: <MdOutlineAccountBox className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.guestPass,
  },
  {
    id: 'loyalty',
    label: 'Loyalty',
    labelKey: 'loyalty',
    icon: <MdOutlineLoyalty className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.loyalty,
  },
  {
    id: 'citations',
    label: 'Citations',
    labelKey: 'citations',
    icon: <MdOutlineWarningAmber className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.citations,
  },
  {
    id: 'parking-validation',
    label: 'Parking Validation',
    labelKey: 'parkingValidation',
    icon: <MdOutlineDiscount className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.parkingValidation,
  },
  {
    id: 'settings',
    label: 'Settings',
    labelKey: 'settings',
    icon: <MdOutlineSettings className="tw:text-font" size={24} />,
    href: MY_ACCOUNT_ROUTES.settings,
  },
];

export const MOBILE_MENU_ITEMS = [
  {
    id: 'home',
    labelKey: 'home',
    label: 'Home',
    icon: <MdOutlineHome size={24} className="tw:text-gray-darker" />,
  },
  {
    id: 'reservations',
    labelKey: 'reservations',
    label: 'Reservations',
    icon: <MdOutlineBook size={24} className="tw:text-gray-darker" />,
  },
  {
    id: 'account',
    labelKey: 'account',
    label: 'Account',
    icon: <MdPersonOutline size={24} className="tw:text-gray-darker" />,
  },
];

/**
 * Footer links that are always shown. Help and About Us are NOT here: they are
 * back-office white-label tabs, resolved per company by `useWhiteLabelNav`.
 */
export const STATIC_FOOTER_LINKS = [
  { labelKey: 'privacyPolicy', label: 'Privacy Policy', href: PUBLIC_ROUTES.privacy },
  { labelKey: 'termsAndConditions', label: 'Terms and Conditions', href: PUBLIC_ROUTES.terms },
];
